/**
 * WebSocket client service for connecting to the backend WebSocket service
 * This integrates with the useMediaAnalytics hook's WebSocket functionality
 */

export interface WebSocketConfig {
  url?: string;
  tenantId: string;
  token: string;
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
  reconnectInterval?: number;
}

export interface WebSocketMessage {
  type: string;
  timestamp?: string;
  data?: any;
  channel?: string;
  mediaId?: string;
}

export interface SubscriptionOptions {
  channel: string;
  mediaId?: string;
}

export interface AnalyticsEventData {
  mediaId: string;
  eventType: string;
  eventData: {
    session_id: string;
    duration_ms?: number;
    position_ms?: number;
    quality?: string;
    event_data?: Record<string, any>;
  };
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private config: Required<WebSocketConfig>;
  private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private messageHandlers = new Map<string, Array<(data: any) => void>>();
  private isConnecting = false;
  private isIntentionallyClosed = false;

  constructor(config: WebSocketConfig) {
    this.config = {
      url: config.url || this.getWebSocketUrl(),
      tenantId: config.tenantId,
      token: config.token,
      autoReconnect: config.autoReconnect ?? true,
      maxReconnectAttempts: config.maxReconnectAttempts ?? 5,
      reconnectInterval: config.reconnectInterval ?? 3000,
    };
  }

  /**
   * Get WebSocket URL based on environment
   */
  private getWebSocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = process.env.NEXT_PUBLIC_WS_HOST || window.location.hostname;
    const port = process.env.NEXT_PUBLIC_WS_PORT || (window.location.protocol === 'https:' ? '443' : '3000');
    
    return `${protocol}//${host}:${port}`;
  }

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    this.isConnecting = true;
    this.isIntentionallyClosed = false;

    try {
      const url = new URL(this.config.url);
      url.searchParams.set('token', this.config.token);
      url.searchParams.set('tenantId', this.config.tenantId);

      this.ws = new WebSocket(url.toString());

      return new Promise((resolve, reject) => {
        if (!this.ws) {
          reject(new Error('Failed to create WebSocket connection'));
          return;
        }

        const onOpen = () => {
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          console.log('WebSocket connected');
          
          // Setup ping interval
          this.startPingInterval();
          
          resolve();
        };

        const onError = (error: Event) => {
          this.isConnecting = false;
          console.error('WebSocket connection error:', error);
          reject(new Error('WebSocket connection failed'));
        };

        const onClose = (event: CloseEvent) => {
          this.isConnecting = false;
          this.stopPingInterval();
          
          console.log('WebSocket disconnected:', event.code, event.reason);
          
          // Attempt reconnection if not intentionally closed
          if (!this.isIntentionallyClosed && this.config.autoReconnect) {
            this.scheduleReconnect();
          }
        };

        const onMessage = (event: MessageEvent) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.addEventListener('open', onOpen);
        this.ws.addEventListener('error', onError);
        this.ws.addEventListener('close', onClose);
        this.ws.addEventListener('message', onMessage);
      });
    } catch (error) {
      this.isConnecting = false;
      throw error;
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isIntentionallyClosed = true;
    this.stopPingInterval();
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }

  /**
   * Send message to server
   */
  private send(message: WebSocketMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, cannot send message:', message);
      return false;
    }

    try {
      this.ws.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error('Failed to send WebSocket message:', error);
      return false;
    }
  }

  /**
   * Subscribe to a channel
   */
  subscribe(options: SubscriptionOptions): boolean {
    return this.send({
      type: 'subscribe',
      channel: options.channel,
      mediaId: options.mediaId,
    });
  }

  /**
   * Unsubscribe from a channel
   */
  unsubscribe(channel: string): boolean {
    return this.send({
      type: 'unsubscribe',
      channel,
    });
  }

  /**
   * Send analytics event
   */
  sendAnalyticsEvent(data: AnalyticsEventData): boolean {
    return this.send({
      type: 'analytics_event',
      data,
    });
  }

  /**
   * Add event handler for specific message types
   */
  on(messageType: string, handler: (data: any) => void): void {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, []);
    }
    this.messageHandlers.get(messageType)!.push(handler);
  }

  /**
   * Remove event handler
   */
  off(messageType: string, handler: (data: any) => void): void {
    const handlers = this.messageHandlers.get(messageType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Handle incoming messages
   */
  private handleMessage(message: WebSocketMessage): void {
    const handlers = this.messageHandlers.get(message.type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message.data || message);
        } catch (error) {
          console.error('Error in WebSocket message handler:', error);
        }
      });
    }

    // Handle pong responses
    if (message.type === 'pong') {
      this.lastPongReceived = Date.now();
    }
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      console.error('Max reconnect attempts reached, giving up');
      return;
    }

    const delay = Math.min(
      this.config.reconnectInterval * Math.pow(2, this.reconnectAttempts),
      30000
    );

    console.log(`Scheduling WebSocket reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect().catch(error => {
        console.error('Reconnection failed:', error);
      });
    }, delay);
  }

  /**
   * Ping interval management
   */
  private pingInterval: NodeJS.Timeout | null = null;
  private lastPongReceived = Date.now();

  private startPingInterval(): void {
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Check if we haven't received a pong in a while
        if (Date.now() - this.lastPongReceived > 45000) { // 45 seconds
          console.warn('WebSocket connection seems stale, reconnecting...');
          this.ws.close(1001, 'Connection stale');
          return;
        }

        this.send({ type: 'ping' });
      }
    }, 30000); // Ping every 30 seconds
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Get connection status
   */
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get connection state
   */
  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  /**
   * Get connection state as string
   */
  get connectionState(): string {
    switch (this.readyState) {
      case WebSocket.CONNECTING: return 'connecting';
      case WebSocket.OPEN: return 'open';
      case WebSocket.CLOSING: return 'closing';
      case WebSocket.CLOSED: return 'closed';
      default: return 'unknown';
    }
  }
}

/**
 * Create a WebSocket client instance with authentication
 */
export const createWebSocketClient = (tenantId: string, token: string): WebSocketClient => {
  return new WebSocketClient({
    tenantId,
    token,
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectInterval: 3000,
  });
};

/**
 * WebSocket client for media analytics
 * This is what the useMediaAnalytics hook should use internally
 */
export const createMediaAnalyticsClient = (
  tenantId: string, 
  token: string, 
  mediaId: string
): WebSocketClient => {
  const client = createWebSocketClient(tenantId, token);
  
  // Auto-subscribe to media channel once connected
  client.on('connection_established', () => {
    client.subscribe({
      channel: `media:${mediaId}`,
      mediaId,
    });
  });

  return client;
};

export default WebSocketClient;